/**
 * Catálogo de reportes del **centro de reportes** (`/reportes`).
 *
 * Es la lista completa del índice del panel anterior
 * (`views/menu/reportes/index.php`): reportes agrupados en seis familias. RIC
 * no se incluye aquí porque pertenece al menú de Monitoreo.
 * Cada entrada guarda la ruta heredada (`Reportes/Turnos`, `Producto/Diferencia_diaria`…)
 * y, cuando el reporte ya vive en el panel nuevo, la **ruta interna** a la que se
 * entra en lugar de saltar al panel anterior.
 *
 * El mapeo es **conservador**: sólo se enlaza al panel nuevo cuando la pantalla es
 * el puerto de ese mismo reporte (comprobado en la cabecera de cada pantalla y en
 * los controladores de la API). Los que se parecen a otra pantalla pero vienen de
 * otro controlador —`Reportes/Nota_venta` frente a los canjes masivos de notas,
 * `Reportes/Reporte_gastos_periodo` frente a la lista de gastos, `Kardex` frente al
 * inventario— siguen enlazando al panel anterior para no llevar a una pantalla
 * distinta.
 */

/** Familia del catálogo (el orden es el del índice anterior). */
export type FamiliaDeReportes =
  | 'General'
  | 'Ventas'
  | 'Liquidaciones'
  | 'Contabilidad'
  | 'Administrativos'
  | 'Otros';

export interface ReporteDelCatalogo {
  familia: FamiliaDeReportes;
  nombre: string;
  /** Ruta del panel anterior (`base_url(ruta)`). */
  ruta: string;
  /** Ruta del panel nuevo, cuando el reporte ya está migrado. */
  interno?: string;
  /** Aclaración corta cuando el nombre heredado no dice todo. */
  nota?: string;
}

export const FAMILIAS: readonly FamiliaDeReportes[] = [
  'General',
  'Ventas',
  'Liquidaciones',
  'Contabilidad',
  'Administrativos',
  'Otros',
];

export const CATALOGO_REPORTES: readonly ReporteDelCatalogo[] = [
  /* ------------------------------ General ------------------------------ */
  {
    familia: 'General',
    nombre: 'Documentos/Mensual',
    ruta: 'Reportes/Documentos',
    interno: '/reportes/documentos',
  },
  {
    familia: 'General',
    nombre: 'Reporte descuento / Recargos - Ventas',
    ruta: 'Reportes/Reporte_descuento_cliente',
    interno: '/reportes/ventas-cliente?descuento=1',
  },
  {
    familia: 'General',
    nombre: 'Reporte descuentos / Recargos - Activos',
    ruta: 'Reportes/Reporte_descuento_clientes_activos',
    interno: '/reportes/descuentos-activos',
  },
  {
    familia: 'General',
    nombre: 'Reporte de placas/clientes',
    ruta: 'Reportes/Reporte_placas_clientes',
    interno: '/reportes/placas-clientes',
  },
  {
    familia: 'General',
    nombre: 'Consumo Interno Ejecutivo',
    ruta: 'Reportes/Consumo_interno_ejecutivo',
    interno: '/reportes/consumo-interno',
  },
  {
    familia: 'General',
    nombre: 'Canjes CardPlus',
    ruta: 'Reportes/Canjes_cardplus_reporte',
    interno: '/logistica/canjes-cardplus',
  },
  {
    familia: 'General',
    nombre: 'Reporte faltante del personal por mes',
    ruta: 'Reportes/Missing',
    interno: '/reportes/faltantes-personal',
  },

  /* ------------------------------ Ventas ------------------------------- */
  {
    familia: 'Ventas',
    nombre: 'Ventas de Productos',
    ruta: 'Reportes/Venta_productos',
    interno: '/reportes/venta-productos',
  },
  {
    familia: 'Ventas',
    nombre: 'Reporte de ventas por cliente',
    ruta: 'Reportes/Ventas_por_cliente',
    interno: '/reportes/ventas-cliente',
  },
  {
    familia: 'Ventas',
    nombre: 'Reporte por serie, fecha y producto',
    ruta: 'Reportes/Reporte_por_serie_fecha_producto',
    interno: '/reportes/serie-fecha-producto',
  },
  {
    familia: 'Ventas',
    nombre: 'Reporte de créditos por cliente',
    ruta: 'Reportes/Creditos_por_cliente',
    interno: '/reportes/creditos-cliente',
  },
  {
    familia: 'Ventas',
    nombre: 'Reporte nota venta por cliente',
    ruta: 'Reportes/Reporte_nota_venta_periodo',
    interno: '/reportes/ventas-cliente?familia=notas&activos=1',
  },
  {
    familia: 'Ventas',
    nombre: 'Reporte de canjes de notas de venta',
    ruta: 'Reportes/Nota_venta',
    interno: '/gestion/administracion/canjes-notas',
  },
  {
    familia: 'Ventas',
    nombre: 'Reportes por mes, diferencia contómetros',
    ruta: 'Producto/Diferencia_diaria',
    interno: '/reportes/diferencia-diaria',
  },
  {
    familia: 'Ventas',
    nombre: 'Pagos por Adelantado',
    ruta: 'Sales/Paid_in_advance',
    interno: '/logistica/pagos-adelantados-documentos',
  },

  /* ---------------------------- Liquidaciones -------------------------- */
  {
    familia: 'Liquidaciones',
    nombre: 'Sistema de liquidación APP',
    ruta: 'Liquidaciones',
    interno: '/reportes/turnos',
  },
  {
    familia: 'Liquidaciones',
    nombre: 'Reporte gastos',
    ruta: 'Reportes/Reporte_gastos_periodo',
    interno: '/reportes/gastos',
  },
  {
    familia: 'Liquidaciones',
    nombre: 'Reporte venta visa',
    ruta: 'Reportes/Reporte_venta_visa_periodo',
    interno: '/reportes/ventas-por-metodo?metodo=VISA',
  },
  {
    familia: 'Liquidaciones',
    nombre: 'Reporte venta Izipay',
    ruta: 'Reportes/Reporte_venta_izipay_periodo',
    interno: '/reportes/ventas-por-metodo?metodo=IZIPAY',
  },
  {
    familia: 'Liquidaciones',
    nombre: 'Reporte venta transferencia',
    ruta: 'Reportes/Reporte_venta_transferencia_periodo',
    interno: '/reportes/ventas-por-metodo?metodo=TRANSFERENCIA',
  },
  {
    familia: 'Liquidaciones',
    nombre: 'Reporte de extras en ventas',
    ruta: 'Reportes/Ventas_extras',
    interno: '/reportes/ventas-cliente?familia=documentos&extras=1',
  },
  {
    familia: 'Liquidaciones',
    nombre: 'Reporte de ventas bonus',
    ruta: 'Reportes/Ventas_por_cliente_bonus',
    interno: '/reportes/ventas-cliente?metodo_pago=BONUS',
  },

  /* ---------------------------- Contabilidad --------------------------- */
  {
    familia: 'Contabilidad',
    nombre: 'Compra de Productos (detalles)',
    ruta: 'Reportes/Compra_productos',
    interno: '/reportes/compra-productos',
  },
  {
    familia: 'Contabilidad',
    nombre: 'Reporte de Movimientos en Cuenta',
    ruta: 'Cuentas/Movimientos',
    interno: '/reportes/movimientos-cuenta',
  },
  {
    familia: 'Contabilidad',
    nombre: 'Reporte de facturas pendientes de pagos',
    ruta: 'Reportes/Reporte_facturas_pagos_pendientes',
    interno: '/reportes/facturas-pendientes',
  },
  { familia: 'Contabilidad', nombre: 'Nubecont detallado', ruta: 'Reportes/Nubecont', interno: '/reportes/nubecont' },
  {
    familia: 'Contabilidad',
    nombre: 'Kardex Valorizado',
    ruta: 'Inventory/Kardex_valorizado',
    interno: '/reportes/kardex?vista=valorizado',
  },
  { familia: 'Contabilidad', nombre: 'Kardex Individual', ruta: 'Kardex', interno: '/reportes/kardex' },

  /* --------------------------- Administrativos ------------------------- */
  {
    familia: 'Administrativos',
    nombre: 'Reporte series globales (UNIFICADO)',
    ruta: 'Reportes/Reporte_series_globales_mes_unificado',
    interno: '/reportes/series-globales-mes',
  },
  {
    familia: 'Administrativos',
    nombre: 'Reporte series globales (COMBUSTIBLES)',
    ruta: 'Reportes/Reporte_series_globales_mes',
    interno: '/reportes/series-globales-mes',
    nota: 'en la pantalla nueva se elige el tipo de producto',
  },
  {
    familia: 'Administrativos',
    nombre: 'Reporte series globales (EXTRAS)',
    ruta: 'Reportes/Reporte_series_globales_mes_extras',
    interno: '/reportes/series-globales-mes',
    nota: 'en la pantalla nueva se elige el tipo de producto',
  },
  {
    familia: 'Administrativos',
    nombre: 'Medición de Tanques Controlador',
    ruta: 'Reportes/Medicion_tanques',
    interno: '/monitoreo/medicion-tanques',
  },
  {
    familia: 'Administrativos',
    nombre: 'Conciliación Surtidores-Tanques',
    ruta: 'Reportes/Conciliacion_surtidores',
    interno: '/monitoreo/conciliacion',
  },
  {
    familia: 'Administrativos',
    nombre: 'Reporte Controlador Ventas',
    ruta: 'Controlador',
    interno: '/monitoreo/pts2',
  },
  {
    familia: 'Administrativos',
    nombre: 'Reporte de Ventas GNV',
    ruta: 'Reportes/Reporte_gnv',
    interno: '/reportes/ventas-gnv',
  },

  /* ------------------------------- Otros ------------------------------- */
  { familia: 'Otros', nombre: 'Bitácora IZIPAY', ruta: 'Reportes/Bitacora_izipay', interno: '/reportes/bitacora-izipay' },
  {
    familia: 'Otros',
    nombre: 'Historial de Cambios de Precios',
    ruta: 'Reportes/Historial_precios',
    interno: '/reportes/historial-precios',
  },
  {
    familia: 'Otros',
    nombre: 'Reporte productividad por turnos',
    ruta: 'Reportes/Productividad_por_shift',
    interno: '/reportes/productividad-turnos',
  },
  {
    familia: 'Otros',
    nombre: 'Reporte descuento - Facturas y Boletas',
    ruta: 'Reportes/Reporte_descuento_periodo',
    interno: '/reportes/ventas-cliente?descuento=1&familia=documentos',
  },
  {
    familia: 'Otros',
    nombre: 'Reporte descuento - Notas Venta y Créditos',
    ruta: 'Reportes/Reporte_descuento_periodo_interno',
    interno: '/reportes/ventas-cliente?descuento=1&familia=interno',
  },
  {
    familia: 'Otros',
    nombre: 'Entrada bóveda por periodo y usuario',
    ruta: 'Reportes/Reporte_boveda_periodo_usuario',
    interno: '/reportes/ingreso-boveda',
  },
  {
    familia: 'Otros',
    nombre: 'Reporte visa por mes',
    ruta: 'Reportes/Reporte_visa_diaria',
    interno: '/reportes/ventas-por-metodo?metodo=VISA&vista=dia',
  },
  {
    familia: 'Otros',
    nombre: 'Reporte transferencia por mes',
    ruta: 'Reportes/Reporte_transferencia_diaria',
    interno: '/reportes/ventas-por-metodo?metodo=TRANSFERENCIA&vista=dia',
  },
];
